import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminSettings, adminQbo } from '../../services/api';

// ── Status badge helpers ────────────────────────────────────────────────────────

function SyncBadge({ status, held }) {
  if (status === 'synced')  return <span className="badge bg-success">Synced</span>;
  if (status === 'failed')  return <span className="badge bg-danger">Failed</span>;
  if (status === 'pending' && held)
    return <span className="badge bg-warning text-dark">Held — No QBO ID</span>;
  if (status === 'pending')
    return <span className="badge bg-warning text-dark">Pending</span>;
  return <span className="badge bg-secondary">Not Pushed</span>;
}

function VoidBadge({ status }) {
  if (!status || status === 'not_applicable') return null;
  if (status === 'voided')             return <span className="badge bg-secondary ms-1">Voided</span>;
  if (status === 'credit_memo_issued') return <span className="badge bg-info ms-1">Credit Memo</span>;
  if (status === 'failed')             return <span className="badge bg-danger ms-1">Void Failed</span>;
  return null;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function QBOSettings() {
  const [searchParams] = useSearchParams();

  // Connection status
  const [connected,     setConnected]     = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Wine item ID setting
  const [wineItemId,     setWineItemId]     = useState('');
  const [wineItemSaving, setWineItemSaving] = useState(false);
  const [wineItemSaved,  setWineItemSaved]  = useState(false);

  // Orders data
  const [orders,        setOrders]        = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // Failed payments: orders that have at least one payment sync failure
  const [failedPayments, setFailedPayments] = useState([]);

  // Active tab
  const [activeTab, setActiveTab] = useState('synced');

  // Per-row retry state: { [orderID]: 'loading' | 'done' | 'error' }
  const [retryState,  setRetryState]  = useState({});
  const [retryErrors, setRetryErrors] = useState({});

  // ── Load connection status ─────────────────────────────────────────────────

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/qbo/status`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('wholesaleToken')}` },
    })
      .then((r) => r.json())
      .then((d) => setConnected(d.connected))
      .catch(() => setConnected(false))
      .finally(() => setStatusLoading(false));
  }, []);

  // ── Load wine item ID ─────────────────────────────────────────────────────

  useEffect(() => {
    adminSettings.getSystem()
      .then((res) => setWineItemId(res.data.settings?.qbo_wine_item_id || ''))
      .catch(() => {});
  }, []);

  // ── Load orders ───────────────────────────────────────────────────────────

  const loadOrders = useCallback(() => {
    setOrdersLoading(true);
    adminQbo.getOrders()
      .then((res) => {
        const rows = res.data.orders || [];
        setOrders(rows);
        // Derive failed payments list from orders that have payment failures
        // (We don't have a separate endpoint, so this is surfaced via order-level notes)
        setFailedPayments(rows.filter((o) => o.has_failed_payments));
      })
      .catch(() => {})
      .finally(() => setOrdersLoading(false));
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ── Derived order segments ────────────────────────────────────────────────

  const syncedOrders  = orders.filter((o) => o.qbo_sync_status === 'synced');
  const heldOrders    = orders.filter((o) => o.qbo_sync_status === 'pending' && o.qbo_hold);
  const pendingOrders = orders.filter((o) => o.qbo_sync_status === 'pending' && !o.qbo_hold);
  const failedOrders  = orders.filter((o) => o.qbo_sync_status === 'failed' || (!o.qbo_sync_status && o.orderStatus !== 'cancelled'));

  // ── Connect to QBO ────────────────────────────────────────────────────────

  const connectToQBO = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/qbo/connect`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('wholesaleToken')}` },
      });
      const data = await res.json();
      window.location.href = data.authUrl;
    } catch (err) {
      alert('Failed to start QuickBooks connection: ' + err.message);
    }
  };

  // ── Save wine item ID ─────────────────────────────────────────────────────

  const saveWineItemId = async () => {
    setWineItemSaving(true);
    setWineItemSaved(false);
    try {
      await adminSettings.updateSystem({ qbo_wine_item_id: wineItemId });
      setWineItemSaved(true);
      setTimeout(() => setWineItemSaved(false), 3000);
    } catch {
      // swallow
    } finally {
      setWineItemSaving(false);
    }
  };

  // ── Retry QBO sync ────────────────────────────────────────────────────────

  const retryOrder = async (orderId) => {
    setRetryState((s) => ({ ...s, [orderId]: 'loading' }));
    setRetryErrors((e) => ({ ...e, [orderId]: null }));

    try {
      const res = await adminQbo.retryOrder(orderId);
      const data = res.data;

      if (data.success) {
        setOrders((prev) => prev.map((o) =>
          o.orderID === orderId
            ? { ...o, qbo_sync_status: 'synced', qbo_invoice_number: data.qboInvoiceNumber,
                qbo_synced_at: new Date().toISOString(), qbo_sync_error: null, qbo_hold: false }
            : o
        ));
        setRetryState((s) => ({ ...s, [orderId]: 'done' }));
      } else {
        const msg = data.error || 'Sync failed';
        setRetryErrors((e) => ({ ...e, [orderId]: msg }));
        setRetryState((s) => ({ ...s, [orderId]: 'error' }));
        // If the order was moved to held state, update row
        if (data.held) {
          setOrders((prev) => prev.map((o) =>
            o.orderID === orderId ? { ...o, qbo_hold: true } : o
          ));
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Network error';
      setRetryErrors((e) => ({ ...e, [orderId]: msg }));
      setRetryState((s) => ({ ...s, [orderId]: 'error' }));
    }
  };

  // ── Shared table columns ──────────────────────────────────────────────────

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const fmtAmt  = (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : '—';

  function OrderRow({ o, showRetry }) {
    const state = retryState[o.orderID];
    const err   = retryErrors[o.orderID];
    return (
      <tr>
        <td>
          <a href={`/admin/orders/${o.orderID}`} className="text-decoration-none fw-medium">
            #{o.orderID}
          </a>
        </td>
        <td>
          <a href={`/admin/customers/${o.customerID}`} className="text-decoration-none">
            {o.companyName}
          </a>
          {!o.qbo_customer_id && (
            <span className="badge bg-warning text-dark ms-1 small" title="No QBO Customer ID assigned">No QBO ID</span>
          )}
        </td>
        <td>{fmtAmt(o.totalAmount)}</td>
        <td>
          <span className={`badge ${
            o.orderStatus === 'confirmed' ? 'bg-info text-dark' :
            o.orderStatus === 'invoiced'  ? 'bg-secondary' :
            o.orderStatus === 'paid'      ? 'bg-dark' :
            o.orderStatus === 'shipped'   ? 'bg-primary' :
            o.orderStatus === 'cancelled' ? 'bg-danger' :
            'bg-warning text-dark'
          }`}>{o.orderStatus}</span>
        </td>
        <td>
          <SyncBadge status={o.qbo_sync_status} held={!!o.qbo_hold} />
          <VoidBadge status={o.qbo_void_status} />
        </td>
        <td>{o.qbo_invoice_number || '—'}</td>
        <td>{o.qbo_terms || '—'}</td>
        <td>{fmtDate(o.qbo_synced_at)}</td>
        <td>
          {showRetry && (
            <div>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => retryOrder(o.orderID)}
                disabled={state === 'loading'}
              >
                {state === 'loading'
                  ? <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
                  : 'Retry'}
              </button>
              {state === 'error' && err && (
                <div className="text-danger small mt-1" style={{ maxWidth: 220 }}>{err}</div>
              )}
              {state === 'done' && (
                <span className="text-success small">✓ Synced</span>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  }

  const tableHead = (
    <thead className="table-light">
      <tr>
        <th>Order</th>
        <th>Company</th>
        <th>Amount</th>
        <th>Status</th>
        <th>QBO Status</th>
        <th>Invoice #</th>
        <th>Terms</th>
        <th>Synced At</th>
        <th>Actions</th>
      </tr>
    </thead>
  );

  const emptyRow = (msg) => (
    <tr><td colSpan={9} className="text-center text-muted py-4">{msg}</td></tr>
  );

  const connectedParam = searchParams.get('connected');
  const errorParam     = searchParams.get('error');

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-4 fw-semibold">QuickBooks Integration</h4>

      {/* OAuth callback banners */}
      {connectedParam === 'true' && (
        <div className="alert alert-success alert-dismissible fade show">
          QuickBooks connected successfully.
          <button type="button" className="btn-close" data-bs-dismiss="alert" />
        </div>
      )}
      {errorParam === 'auth_failed' && (
        <div className="alert alert-danger alert-dismissible fade show">
          QuickBooks connection failed. Please try again.
          <button type="button" className="btn-close" data-bs-dismiss="alert" />
        </div>
      )}

      {/* ── Connection status ────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header bg-light"><strong>QuickBooks Online</strong></div>
        <div className="card-body">
          {statusLoading ? (
            <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          ) : (
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {connected
                ? <span className="badge bg-success fs-6">Connected</span>
                : <span className="badge bg-danger fs-6">Not Connected</span>}
              {connected ? (
                <button className="btn btn-outline-secondary btn-sm" onClick={connectToQBO}>Reconnect</button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={connectToQBO}>Connect to QuickBooks</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Line item configuration ───────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header bg-light"><strong>Invoice Line Item Configuration</strong></div>
        <div className="card-body">
          <p className="text-muted mb-3" style={{ maxWidth: 600 }}>
            All wine sales are mapped to a single QuickBooks service item. Enter the Item ID
            from your QuickBooks Products &amp; Services list.
          </p>
          <div className="row g-2 align-items-end" style={{ maxWidth: 400 }}>
            <div className="col">
              <label className="form-label fw-medium">QBO Wine Item ID</label>
              <input
                className="form-control"
                value={wineItemId}
                onChange={(e) => setWineItemId(e.target.value)}
                placeholder="e.g. 21"
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-primary"
                onClick={saveWineItemId}
                disabled={wineItemSaving}
              >
                {wineItemSaving
                  ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                  : 'Save'}
              </button>
            </div>
          </div>
          {wineItemSaved && <div className="text-success mt-2 small">Item ID saved.</div>}
        </div>
      </div>

      {/* ── Invoice sync status ───────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header bg-light d-flex align-items-center justify-content-between flex-wrap gap-2">
          <strong>Invoice Sync Status</strong>
          <button className="btn btn-outline-secondary btn-sm" onClick={loadOrders}>
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="card-header border-top-0 py-0">
          <ul className="nav nav-tabs card-header-tabs">
            {[
              { key: 'synced',  label: `Synced (${syncedOrders.length})`,  cls: 'text-success' },
              { key: 'pending', label: `Pending (${pendingOrders.length})`, cls: 'text-warning' },
              { key: 'held',    label: `Held — No QBO ID (${heldOrders.length})`, cls: heldOrders.length ? 'text-danger fw-semibold' : '' },
              { key: 'failed',  label: `Failed (${failedOrders.length})`,  cls: failedOrders.length  ? 'text-danger fw-semibold' : '' },
            ].map(({ key, label, cls }) => (
              <li key={key} className="nav-item">
                <button
                  className={`nav-link ${activeTab === key ? 'active' : ''} ${cls}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-body p-0">
          {ordersLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-secondary" role="status" />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                {tableHead}
                <tbody>
                  {/* Synced tab */}
                  {activeTab === 'synced' && (
                    syncedOrders.length === 0
                      ? emptyRow('No synced orders.')
                      : syncedOrders.map((o) => <OrderRow key={o.orderID} o={o} showRetry={false} />)
                  )}

                  {/* Pending tab */}
                  {activeTab === 'pending' && (
                    pendingOrders.length === 0
                      ? emptyRow('No orders awaiting initial sync.')
                      : pendingOrders.map((o) => <OrderRow key={o.orderID} o={o} showRetry={true} />)
                  )}

                  {/* Held tab */}
                  {activeTab === 'held' && (
                    heldOrders.length === 0
                      ? emptyRow('No held orders. All companies with synced orders have a QBO Customer ID assigned.')
                      : heldOrders.map((o) => (
                          <React.Fragment key={o.orderID}>
                            <OrderRow o={o} showRetry={true} />
                            {!o.qbo_customer_id && (
                              <tr className="table-warning">
                                <td colSpan={9} className="py-1 ps-5 small text-warning-emphasis">
                                  <strong>Action required:</strong> Go to the{' '}
                                  <a href={`/admin/customers/${o.customerID}`}>
                                    {o.companyName} customer page
                                  </a>{' '}
                                  and assign a QBO Customer ID. Then click Retry above.
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                  )}

                  {/* Failed tab */}
                  {activeTab === 'failed' && (
                    failedOrders.length === 0
                      ? emptyRow('No failed orders.')
                      : failedOrders.map((o) => (
                          <React.Fragment key={o.orderID}>
                            <OrderRow o={o} showRetry={true} />
                            {o.qbo_sync_error && (
                              <tr className="table-danger">
                                <td colSpan={9} className="py-1 ps-5 small text-danger-emphasis">
                                  {o.qbo_sync_error}
                                </td>
                              </tr>
                            )}
                            {o.qbo_void_status === 'failed' && (
                              <tr className="table-danger">
                                <td colSpan={9} className="py-1 ps-5 small text-danger-emphasis">
                                  <strong>Manual QBO action required:</strong> Could not void the invoice or create a credit memo automatically after cancellation.
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
